import { supabase } from "@/integrations/supabase/client";
import type { DBNotification } from "./types";

/**
 * Centralised React Query keys for the notification feature.
 *
 * Kept as `as const` tuples so TypeScript can narrow the key shape at
 * call sites — in particular, `queryClient.setQueryData<DBNotification[]>(INBOX_KEY, …)`
 * infers the data type from the key without a manual generic.
 */
export const INBOX_KEY   = ["notifications", "inbox"]   as const;
export const HISTORY_KEY = ["notifications", "history"] as const;

/**
 * Fetcher for the Inbox tab — notifications that are NOT archived.
 *
 * Ordering is newest-first; the 50-row limit matches the popover + sheet
 * expectations (the popover only renders the top 4, the sheet shows the
 * full list with overflow scroll).
 */
export async function fetchInbox(): Promise<DBNotification[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("is_archived", false)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as DBNotification[];
}

/**
 * Fetcher for the History tab — archived notifications.
 *
 * Intentionally lazy: only fires when the user opens the History tab for
 * the first time (the calling `useQuery` sets `enabled` accordingly).
 * Capped at 100 rows, which is plenty for a workspace inbox and keeps
 * initial render fast.
 */
export async function fetchHistory(): Promise<DBNotification[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("is_archived", true)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as DBNotification[];
}
